import { useEffect, useState, useMemo } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { theme, darkTheme } from '@/styles/theme';
import { AppRouter } from '@/router';
import { useAuthStore } from '@/store';
import { GlobalLoader } from '@/components/common/GlobalLoader';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { PromptDialog } from '@/components/common/PromptDialog';
import { AlertContainer } from '@/components/common/AlertContainer';
import { KeyboardShortcuts } from '@/components/common/KeyboardShortcuts';

/**
 * Main App component
 * Sets up theme, routing, and global components
 */
function App() {
  const initializeAuth = useAuthStore((state) => state.initializeAuth);
  const logout = useAuthStore((state) => state.logout);

  // Dark mode
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem('theme') === 'dark';
  });

  const currentTheme = useMemo(() => isDarkMode ? darkTheme : theme, [isDarkMode]);

  // Listen for theme toggle events from Header
  useEffect(() => {
    const handleToggle = () => {
      setIsDarkMode(prev => {
        const newVal = !prev;
        localStorage.setItem('theme', newVal ? 'dark' : 'light');
        return newVal;
      });
    };
    window.addEventListener('theme:toggle', handleToggle);
    return () => window.removeEventListener('theme:toggle', handleToggle);
  }, []);

  // Initialize auth state from storage on app load
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // The login lives in localStorage and is shared by all tabs: keep tabs in sync
  // when another tab logs in or out.
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== 'auth-token' && event.key !== null) return;
      if (event.newValue) {
        initializeAuth();
      } else if (window.location.pathname !== '/login') {
        logout();
        window.location.href = '/login';
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [initializeAuth, logout]);

  // Listen for auth events from axios interceptors
  useEffect(() => {
    const handleUnauthorized = () => {
      logout();
      sessionStorage.clear();
      localStorage.removeItem('workspace-storage');
      localStorage.removeItem('auth-storage');
      window.location.href = '/login';
    };

    const handleForbidden = () => {
      // Could show a toast or redirect to an error page
      console.warn('Access forbidden');
    };

    const handleServerError = (event: CustomEvent) => {
      console.error('Server error:', event.detail);
    };

    const handleNetworkError = () => {
      console.error('Network error - please check your connection');
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    window.addEventListener('auth:forbidden', handleForbidden);
    window.addEventListener('server:error', handleServerError as EventListener);
    window.addEventListener('network:error', handleNetworkError);

    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
      window.removeEventListener('auth:forbidden', handleForbidden);
      window.removeEventListener('server:error', handleServerError as EventListener);
      window.removeEventListener('network:error', handleNetworkError);
    };
  }, [logout]);

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <AppRouter />
      <GlobalLoader />
      <ConfirmDialog />
      <PromptDialog />
      <AlertContainer />
      <KeyboardShortcuts />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </ThemeProvider>
  );
}

export default App;
